import { useCallback, useEffect, useMemo } from "react";
import { t } from "i18next";
import { cn } from "@edusogno/lib/utils";
import { uuid } from "@/utils/string.ts";
import { useAgenda } from "@/contexts/AgendaContext.tsx";
import { useUserEventsAndMaterials } from "@/data/user.ts";
import {
  getTime,
  generateSlots,
  INTERNAL_DATE_FORMAT,
} from "@/utils/agenda.ts";
import Slot from "./week/Slot.tsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat.js";
import { useTimezone } from "@/contexts/TimezoneContext";
dayjs.extend(customParseFormat);
const totalTimeInMinutes = 14 * 60 * 60 * 1000;

const times = getTime();

const WeekView: React.FC = () => {
  const { selectedTimezone } = useTimezone();
  const { week } = useAgenda();
  const { eventMaterials } = useUserEventsAndMaterials();

  const slots = useMemo(
    () => generateSlots(week, eventMaterials ? eventMaterials.item.events : [], selectedTimezone),
    [week, eventMaterials, selectedTimezone],
  );

  const updateTimeLine = useCallback(() => {
    const timelineElement = document.getElementById(
      "timeline",
    ) as HTMLDivElement;
    const startTime = dayjs().hour(8).minute(0);
    const currentTime = dayjs();
    const gridElement = document.getElementById("slotGrid");

    const calendarGrid = document.getElementById(
      "calendarGrid",
    ) as HTMLDivElement;

    if (
      startTime.isSame(currentTime, "minute") ||
      startTime.isBefore(currentTime, "minute")
    ) {
      if (gridElement !== null && timelineElement !== null) {
        const dimensions = gridElement.getBoundingClientRect();
        const translateY = Math.floor(
          (currentTime.diff(startTime) / totalTimeInMinutes) *
          dimensions.height,
        );

        if (calendarGrid !== null) {
          const calendarGridDim = calendarGrid.getBoundingClientRect();

          const timeGrid = (calendarGridDim.width - dimensions.width).toFixed(
            2,
          );
          const weekGrid = document.getElementById(
            "weeksGrid",
          ) as HTMLDivElement;

          if (weekGrid !== null) {
            weekGrid.style.marginLeft = `${timeGrid}px`;
          }
        }

        timelineElement.style.cssText = `transform:translateY(${translateY}px);`;
      }
    }
  }, []); 

  useEffect(() => {
    const handler = () => {
      updateTimeLine();
      const subText = document.getElementById("not");
      const grid = document.getElementById("slotGrid") as HTMLDivElement;
      if (subText !== null && grid !== null) {
        const dimensions = (
          grid.childNodes[0] as HTMLElement
        ).getBoundingClientRect();
        subText.style.setProperty("--av-b-h", `${dimensions.height}px`);
        subText.style.setProperty("--av-b-w", `${dimensions.width}px`);
      }
    };

    handler();
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, [updateTimeLine]); 

  useEffect(() => {
    updateTimeLine();
  }, [week, slots, updateTimeLine]); 

  return (
    <div className={"w-full h-full flex flex-col"}>
      <div id="weeksGrid" className={"flex flex-col gap-2 h-full"}>
        <div className="flex flex-col gap-2 mb-1">
          <div className={"grid grid-cols-7 gap-0.5 rounded h-4 pl-16"}>
            {week.map((day, idx) => (
              <p
                className={cn(
                  "text-center text-light uppercase font-semibold text-xs",
                  {
                    "flex items-center justify-center bg-primary  rounded-[3px]":
                      dayjs(day, INTERNAL_DATE_FORMAT).isSame(dayjs(), "day"), 
                  },
                )}
                key={uuid()}
              >
                {t(`weeks.short.${idx + 1}`)}
                &nbsp;
                {dayjs(day, INTERNAL_DATE_FORMAT).format("DD")} 
              </p>
            ))}
          </div>
        </div>
        <div className="relative flex flex-row h-full gap-x-6">
          <div
            className={
              "grid grid-cols-1  grid-rows-[repeat(14,minmax(0,.5fr))] items-center"
            }
            id="timeGrid"
          >
            {times.map((i) => (
              <p
                className={"self-start text-center text-light font-semibold"}
                key={i}
              >
                {i}
              </p>
            ))}
          </div>
          <div
            id={"slotGrid"}
            className={
              "grid grid-cols-[repeat(7,calc(calc(100%_-_12px)_/_7))] w-[--w]  [--w:calc(100%_-_48px)]  gap-0.5 relative"
            }
          >
            {slots.map((column, idx) => (
              <div
                key={"column-week" + idx}
                className={cn(
                  "grid-rows-[repeat(14,calc(100%_/_14))]  grid-cols-1 relative bg-white grid w-full ",
                  {
                    "bg-[#56438A]": dayjs(
                      week[idx],
                      INTERNAL_DATE_FORMAT,
                    ).isSame(dayjs(), "day"), // Use the passed timezone
                  },
                )}
              >
                {dayjs(week[idx], INTERNAL_DATE_FORMAT).isSame(
                  dayjs(), // Use the passed timezone
                  "day",
                ) && (
                    <div
                      id="timeline"
                      className={
                        "absolute  h-0.5  w-full z-10 bg-[#FFE605] before:block before:rounded-full before:w-3 before:h-3 before:absolute before:-left-3 before:-top-1 before:bg-[#FFE605]"
                      }
                    />
                  )}
                {column.map((i, id) => (
                  <Slot key={uuid() + id} data-col={`${idx}-${id}`} slot={i} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekView;